import { useState, useMemo, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
  alpha,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import { PricingTier, TierName } from '../../types/b2b';

const fetchPricingTiers = async () => {
  const response = await axiosInstance.get('/pricing-tiers');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const filterOptions: FilterOption[] = [
  {
    field: 'isActive',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Inactive' },
    ],
  },
  {
    field: 'tierName',
    label: 'Tier',
    type: 'select',
    options: [
      { value: 'bronze', label: 'Bronze' },
      { value: 'silver', label: 'Silver' },
      { value: 'gold', label: 'Gold' },
      { value: 'platinum', label: 'Platinum' },
      { value: 'custom', label: 'Custom' },
    ],
  },
];

const exportColumns: ColumnConfig[] = [
  { field: 'tierName', headerName: 'Tier Name' },
  {
    field: 'customer',
    headerName: 'Customer',
    valueFormatter: (_, row) => row.customer ? `${row.customer.firstName} ${row.customer.lastName}` : 'All Customers',
  },
  {
    field: 'product',
    headerName: 'Product',
    valueFormatter: (_, row) => row.product?.name || 'All Products',
  },
  { field: 'minimumQuantity', headerName: 'Min Qty' },
  { field: 'maximumQuantity', headerName: 'Max Qty' },
  {
    field: 'discount',
    headerName: 'Discount',
    valueFormatter: (_, row) => {
      if (row.discountType === 'percentage') {
        return `${row.discountValue}%`;
      }
      return `${row.discountValue} DZD`;
    },
  },
  {
    field: 'isActive',
    headerName: 'Status',
    valueFormatter: (value) => value ? 'Active' : 'Inactive',
  },
];

const getTierColor = (tier: TierName): string => {
  switch (tier) {
    case 'bronze':
      return '#CD7F32';
    case 'silver':
      return '#C0C0C0';
    case 'gold':
      return '#FFD700';
    case 'platinum':
      return '#E5E4E2';
    case 'custom':
      return palette.primary;
    default:
      return palette.textMuted;
  }
};

export const PricingTierList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const { data: pricingTiers, isLoading } = useQuery({
    queryKey: ['pricing-tiers'],
    queryFn: fetchPricingTiers,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.delete(`/pricing-tiers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-tiers'] });
      showMessage('Pricing tier deleted successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to delete pricing tier', 'error');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      axiosInstance.patch(`/pricing-tiers/${id}`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-tiers'] });
      showMessage('Pricing tier updated successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update pricing tier', 'error');
    },
  });

  const filteredData = useMemo(() => {
    if (!pricingTiers) return [];

    return pricingTiers.filter((item: PricingTier) => {
      // Search filter
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const customerName = item.customer
          ? `${item.customer.firstName} ${item.customer.lastName}`
          : '';
        const productName = item.product?.name || '';
        const matchesSearch =
          item.tierName.toLowerCase().includes(search) ||
          customerName.toLowerCase().includes(search) ||
          productName.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (activeFilters.isActive !== undefined && activeFilters.isActive !== '') {
        const isActiveFilter = activeFilters.isActive === 'true';
        if (item.isActive !== isActiveFilter) return false;
      }

      // Tier filter
      if (activeFilters.tierName && item.tierName !== activeFilters.tierName) {
        return false;
      }

      return true;
    });
  }, [pricingTiers, searchValue, activeFilters]);

  const paginatedData = useMemo(() => {
    const startIndex = page * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, page, pageSize]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this pricing tier?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleActive = (id: string, currentStatus: boolean) => {
    toggleActiveMutation.mutate({ id, isActive: !currentStatus });
  };

  const formatDiscount = (tier: PricingTier) => {
    if (tier.discountType === 'percentage') {
      return `${tier.discountValue}%`;
    }
    return `${tier.discountValue.toLocaleString()} DZD`;
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const total = filteredData.length;
    const active = filteredData.filter((item: PricingTier) => item.isActive).length;
    const inactive = total - active;

    return { total, active, inactive };
  }, [filteredData]);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocalOfferIcon sx={{ color: palette.primary, fontSize: 24 }} />
              <Typography
                variant="overline"
                sx={{
                  color: palette.primary,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                B2B PRICING SYSTEM
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              PRICING TIERS
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/app/pricing-tiers/create')}
            sx={{
              backgroundColor: palette.primary,
              color: '#FFFFFF',
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.sm,
              px: 3,
              py: 1.5,
              borderRadius: theme.radius.sm,
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.8),
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: `0 8px 24px ${alpha(palette.primary, 0.3)}`,
              },
            }}
          >
            New Tier
          </Button>
        </Box>
      </Box>

      {/* Stats Dashboard */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3, ...animations.slideUp(0.1) }}>
        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.primary,
            },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            TOTAL TIERS
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.extrabold,
              color: colors.textPrimary,
              lineHeight: 1,
              mt: 1,
            }}
          >
            {stats.total}
          </Typography>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.success,
            },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            ACTIVE
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.extrabold,
              color: palette.success,
              lineHeight: 1,
              mt: 1,
            }}
          >
            {stats.active}
          </Typography>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.warning,
            },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            INACTIVE
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.extrabold,
              color: palette.warning,
              lineHeight: 1,
              mt: 1,
            }}
          >
            {stats.inactive}
          </Typography>
        </Card>
      </Box>

      {/* Search & Filter Bar */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          p: 2,
          mb: 3,
          ...animations.slideUp(0.15),
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="SEARCH PRICING TIERS..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': { border: 'none' },
                '&:hover': {
                  borderColor: colors.borderLight,
                },
                '&.Mui-focused': {
                  borderColor: palette.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
                textTransform: 'uppercase',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
          />
          <TableToolbar
            title=""
            searchValue=""
            onSearchChange={() => {}}
            searchPlaceholder=""
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={exportColumns}
            exportFileName="pricing-tiers"
          />
        </Box>
      </Card>

      {/* Pricing Tiers Table */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          ...animations.slideUp(0.2),
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: palette.primary }} />
          </Box>
        ) : filteredData.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <LocalOfferIcon sx={{ fontSize: 80, color: colors.textMuted, mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                mb: 1,
              }}
            >
              NO PRICING TIERS FOUND
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              Create your first pricing tier to offer volume discounts
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${colors.border}`,
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: colors.surfaceElevated,
                      borderBottom: `2px solid ${palette.primary}`,
                    }}
                  >
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>
                      Tier
                    </TableCell>
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>
                      Customer
                    </TableCell>
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>
                      Product
                    </TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>
                      Qty Range
                    </TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>
                      Discount
                    </TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>
                      Active
                    </TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((tier: PricingTier, index: number) => {
                    const tierColor = getTierColor(tier.tierName);

                    return (
                      <TableRow
                        key={tier.id}
                        sx={{
                          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          borderLeft: `4px solid ${tierColor}`,
                          ...animations.tableRowFade(index),
                          '&:hover': {
                            backgroundColor: alpha(tierColor, 0.05),
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        {/* Tier Name */}
                        <TableCell>
                          <Chip
                            label={tier.tierName.toUpperCase()}
                            sx={{
                              backgroundColor: alpha(tierColor, 0.1),
                              color: tierColor,
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.xs,
                              fontWeight: typography.fontWeight.bold,
                              border: `1px solid ${tierColor}`,
                            }}
                          />
                        </TableCell>

                        {/* Customer */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 18, color: tier.customer ? palette.info : colors.textMuted }} />
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.display,
                                fontWeight: typography.fontWeight.semibold,
                                color: colors.textPrimary,
                                fontSize: typography.fontSize.sm,
                              }}
                            >
                              {tier.customer
                                ? `${tier.customer.firstName} ${tier.customer.lastName}`
                                : 'All Customers'}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Product */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InventoryIcon sx={{ fontSize: 18, color: tier.product ? palette.info : colors.textMuted }} />
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.display,
                                fontWeight: typography.fontWeight.semibold,
                                color: colors.textPrimary,
                                fontSize: typography.fontSize.sm,
                              }}
                            >
                              {tier.product?.name || 'All Products'}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Quantity Range */}
                        <TableCell align="center">
                          <Typography
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.sm,
                              color: colors.textPrimary,
                            }}
                          >
                            {tier.minimumQuantity} - {tier.maximumQuantity || '∞'}
                          </Typography>
                        </TableCell>

                        {/* Discount */}
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              px: 2,
                              py: 0.75,
                              borderRadius: theme.radius.xs,
                              backgroundColor: alpha(palette.success, 0.1),
                              border: `1px solid ${palette.success}`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                fontSize: typography.fontSize.sm,
                                fontWeight: typography.fontWeight.extrabold,
                                color: palette.success,
                              }}
                            >
                              {formatDiscount(tier)}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Active Toggle */}
                        <TableCell align="center">
                          <Switch
                            checked={tier.isActive}
                            onChange={() => handleToggleActive(tier.id, tier.isActive)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: palette.success,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: palette.success,
                              },
                            }}
                          />
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/app/pricing-tiers/edit/${tier.id}`)}
                                sx={{
                                  border: `1px solid ${colors.border}`,
                                  borderRadius: theme.radius.xs,
                                  color: colors.textPrimary,
                                  '&:hover': {
                                    borderColor: palette.primary,
                                    backgroundColor: alpha(palette.primary, 0.1),
                                    color: palette.primary,
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(tier.id)}
                                sx={{
                                  border: `1px solid ${colors.border}`,
                                  borderRadius: theme.radius.xs,
                                  color: colors.textPrimary,
                                  '&:hover': {
                                    borderColor: palette.error,
                                    backgroundColor: alpha(palette.error, 0.1),
                                    color: palette.error,
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                backgroundColor: colors.surfaceElevated,
                borderTop: `2px solid ${colors.border}`,
              }}
            >
              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.mono,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.wide,
                  },
                }}
              />
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};
